import { useEffect, useState, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Divider, Spinner, Heading, Wrap, WrapItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OpeningHoursForm } from '../components/OpeningHoursForm';
import {
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  type LocationDto,
  type CreateLocationRequest,
  type UpdateLocationRequest,
} from '../api/organizationApi';

const VENUE_TYPES = [
  'cafe', 'restaurant', 'shop', 'supermarket', 'hotel',
  'gym', 'spa', 'bar', 'office', 'clinic', 'other',
] as const;

export function LocationsPage() {
  const { t } = useTranslation();
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLocations();
      setLocations(data);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  if (loading) {
    return (
      <Flex flex={1} justify="center" align="center" p={4}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box flex={1} p={4} maxW="900px">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h2" size="lg">{t('locations.title')}</Heading>
        <Button colorScheme="brand" onClick={() => setShowCreate(true)}>
          {t('locations.addLocation')}
        </Button>
      </Flex>

      {error && (
        <Flex bg="red.50" p={3} borderRadius="md" mb={3}>
          <Text color="red.500">{error}</Text>
        </Flex>
      )}
      {success && (
        <Flex bg="green.50" p={3} borderRadius="md" mb={3}>
          <Text color="green.500">{success}</Text>
        </Flex>
      )}

      {showCreate && (
        <LocationForm
          onSave={async (data) => {
            try {
              await createLocation(data);
              setShowCreate(false);
              setSuccess(t('locations.created'));
              await loadLocations();
            } catch (e: unknown) {
              setError((e as Error).message);
            }
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {locations.length === 0 && !showCreate ? (
        <Flex justify="center" p={6}>
          <Text color="gray.500">{t('locations.noLocations')}</Text>
        </Flex>
      ) : (
        <Box>
          {locations.map((loc) => (
            <LocationCard
              key={loc.id}
              location={loc}
              isEditing={editingId === loc.id}
              onEdit={() => setEditingId(loc.id)}
              onCancelEdit={() => setEditingId(null)}
              onSave={async (data) => {
                try {
                  await updateLocation(loc.id, data);
                  setEditingId(null);
                  setSuccess(t('locations.updated'));
                  await loadLocations();
                } catch (e: unknown) {
                  setError((e as Error).message);
                }
              }}
              onDelete={async () => {
                if (!confirm(t('locations.confirmDelete'))) return;
                try {
                  await deleteLocation(loc.id);
                  setSuccess(t('locations.deleted'));
                  await loadLocations();
                } catch (e: unknown) {
                  setError((e as Error).message);
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function LocationCard({
  location,
  isEditing,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
}: {
  location: LocationDto;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (data: UpdateLocationRequest) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hours = useDisclosure();

  if (isEditing) {
    return (
      <LocationForm
        initial={location}
        onSave={async (data) => { await onSave(data); }}
        onCancel={onCancelEdit}
      />
    );
  }

  const address = location.address;
  const addressStr = [address?.streetAddress, address?.city, address?.stateProvince, address?.country]
    .filter(Boolean)
    .join(', ');

  return (
    <Box p={4} borderRadius="md" bg="gray.50" mb={3}>
      <Flex justify="space-between" align="center" mb={2}>
        <Box>
          <Text fontSize="lg" fontWeight="600">{location.name}</Text>
          <Text fontSize="sm" color="gray.600">
            {t(`locations.venueTypes.${location.venueType}`)}
          </Text>
        </Box>
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/organization/locations/${location.id}/schedule`)}
          >
            {t('locations.scheduling')}
          </Button>
          <Button size="sm" variant="outline" onClick={hours.onOpen}>
            {t('locations.openingHours')}
          </Button>
          <Button size="sm" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          <Button size="sm" colorScheme="red" onClick={onDelete}>
            {t('common.delete')}
          </Button>
        </Flex>
      </Flex>
      {addressStr && (
        <Text fontSize="sm" color="gray.600">{addressStr}</Text>
      )}
      {location.timezone && (
        <Text fontSize="xs" color="gray.500">{t('locations.timezone')}: {location.timezone}</Text>
      )}

      <Modal isOpen={hours.isOpen} onClose={hours.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>{t('locations.openingHours')} — {location.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <OpeningHoursForm locationId={location.id} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function LocationForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<LocationDto>;
  onSave: (data: CreateLocationRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateLocationRequest>({
    name: initial?.name ?? '',
    venueType: initial?.venueType ?? 'cafe',
    address: initial?.address ?? {},
    timezone: initial?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={4} borderRadius="md" bg="gray.50" mb={3}>
      <Heading as="h3" size="md" mb={3}>
        {initial ? t('locations.editLocation') : t('locations.addLocation')}
      </Heading>

      <Text fontWeight="600" mb={1}>{t('locations.locationName')}</Text>
      <Input
        value={form.name}
        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
        placeholder={t('locations.namePlaceholder')}
        mb={3}
      />

      <Text fontWeight="600" mb={1}>{t('locations.venueType')}</Text>
      <Wrap spacing={2} mb={3}>
        {VENUE_TYPES.map((vt) => (
          <WrapItem key={vt}>
            <Button
              size="sm"
              colorScheme={form.venueType === vt ? 'brand' : 'gray'}
              variant={form.venueType === vt ? 'solid' : 'outline'}
              onClick={() => setForm((prev) => ({ ...prev, venueType: vt }))}
            >
              {t(`locations.venueTypes.${vt}`)}
            </Button>
          </WrapItem>
        ))}
      </Wrap>

      <Divider my={3} />

      <Text fontWeight="600" mb={1}>{t('organization.streetAddress')}</Text>
      <Input
        value={form.address?.streetAddress ?? ''}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, address: { ...prev.address, streetAddress: e.target.value } }))
        }
        mb={3}
      />

      <Flex gap={3} mb={3}>
        <Box flex={1}>
          <Text fontWeight="600" mb={1}>{t('organization.city')}</Text>
          <Input
            value={form.address?.city ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, address: { ...prev.address, city: e.target.value } }))
            }
          />
        </Box>
        <Box flex={1}>
          <Text fontWeight="600" mb={1}>{t('organization.country')}</Text>
          <Input
            value={form.address?.country ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, address: { ...prev.address, country: e.target.value } }))
            }
          />
        </Box>
      </Flex>

      <Text fontWeight="600" mb={1}>{t('locations.timezone')}</Text>
      <Input
        value={form.timezone ?? ''}
        onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
        placeholder="Europe/Kyiv"
        mb={3}
      />

      <Flex gap={3} justify="flex-end" pt={2}>
        <Button onClick={onCancel} isDisabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button colorScheme="brand" onClick={handleSave} isDisabled={saving || !form.name.trim()}>
          {saving ? <Spinner size="sm" /> : t('common.save')}
        </Button>
      </Flex>
    </Box>
  );
}
